import { Component, OnDestroy } from '@angular/core';
import { CountryDropdownOption } from '@app/analyze-data/constants/analyze-data.constants';
import { CategoryPathsUtilitiesService } from '@app/analyze-data/services/category-paths-utilities.service';
import { ChannelOptionsType } from '@app/analyze-data/types/channel-options.type';
import { AmazonCategory } from '@app/category-taxonomy/models/enums/amazon-category.enum';
import { CategoryPathsCategories } from '@app/category-taxonomy/models/types/category-paths-display.type';
import { CategoryPathsFormatterService } from '@app/category-taxonomy/services/category-paths-formatter.service';
import { CategoryTaxonomyDataService } from '@app/category-taxonomy/services/category-taxonomy-data.service';
import { CategoryPathsRequest } from '@app/category-taxonomy/services/requests/category-paths.request';
import { CategoryPathsResponse } from '@app/category-taxonomy/services/responses/category-paths.response';
import { BootstrapThemeProp } from '@app/core/services/bootstrap-theme.service';
import { Modal } from '@app/modules/modals/modals.module';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil, tap } from 'rxjs';

export interface CategoryPathsModalParams {
    channel: ChannelOptionsType;
    country: CountryDropdownOption;
    databaseId: string;
    /**
     * Either the categories or the category id should be provided, but not both.
     */
    categories?: CategoryPathsCategories;
    /**
     * Either the categories or the category id should be provided, but not both.
     */
    categoryId?: string;
    amazonCategoryType?: AmazonCategory;
}

@Component({
    selector: 'fdx-category-paths-modal',
    templateUrl: './category-paths-modal.component.html',
    styleUrl: './category-paths-modal.component.scss',
    standalone: false
})
export class CategoryPathsModalComponent implements Modal, OnDestroy {
    protected readonly unsubscribe$: Subject<void> = new Subject<void>();

    modalTheme: BootstrapThemeProp = 'secondary';
    channel: ChannelOptionsType;
    country: CountryDropdownOption;
    databaseId: string;
    categories: CategoryPathsCategories;
    categoryId?: string;
    amazonCategoryType?: AmazonCategory;

    loadingCategories: boolean = false;

    get title(): string {
        return `Corresponding ${this.channel?.display} paths`;
    }

    get filterByProductType(): boolean {
        return this.amazonCategoryType === AmazonCategory.ProductType;
    }

    constructor(
        private readonly categoryPathsFormatterService: CategoryPathsFormatterService,
        private readonly categoryPathsUtilitiesService: CategoryPathsUtilitiesService,
        private readonly categoryTaxonomyDataService: CategoryTaxonomyDataService,
        private readonly modal: NgbActiveModal
    ) {}

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    onModalInit(params: CategoryPathsModalParams): void {
        this.channel = params.channel;
        this.country = params.country;
        this.databaseId = params.databaseId;
        this.amazonCategoryType = params.amazonCategoryType;

        if ('categories' in params) {
            this.categories = params.categories;
        } else if ('categoryId' in params) {
            this.categoryId = params.categoryId;
            this.fetchCategories();
        }
    }

    onModalSubmit(_event: MouseEvent): void {
        this.modal.close();
    }

    onModalCancel(_event: MouseEvent): void {
        this.modal.dismiss();
    }

    fetchCategories(): void {
        this.loadingCategories = true;

        const params: CategoryPathsRequest = {
            client: this.country.client,
            region: this.categoryPathsUtilitiesService.getCategoryPathsRegion(this.country),
            category_id: this.categoryId
        };

        if (this.amazonCategoryType) {
            params.amazon_category = this.amazonCategoryType;
        }

        this.categoryTaxonomyDataService.getCategoryPaths(this.databaseId, params).pipe(
            tap((response: CategoryPathsResponse) => {
                const formattedCategories = this.categoryPathsFormatterService.formatCategoryPaths(response);

                if (formattedCategories.length > 0) {
                    this.categories = formattedCategories[0];
                } else {
                    this.categories = [];
                }
            }),
            finalize(() => {
                this.loadingCategories = false;
            }),
            takeUntil(this.unsubscribe$)
        )
        .subscribe();
    }
}
